import { useEffect, useMemo, useRef, useState } from "react";

import { StoryContent, StoryScene } from "../../../types/storyMap";
import {
  useAttachStorySceneImages,
  useUploadStoryMedia,
  useUploadStoryPdf,
} from "../../../api/storyMaps";
import { useStorySceneSources } from "../useStorySceneLayers";
import { useSceneCaptures } from "./useSceneCaptures";

export type PublishAssetsPhase =
  | "idle"
  | "capturing"
  | "building_pdf"
  | "uploading"
  | "done"
  | "failed";

export interface PublishAssetsState {
  phase: PublishAssetsPhase;
  /** Human-readable progress line for the publish dialog. */
  detail: string;
  /** Render this while it runs — the hidden capture map lives inside it. */
  runner: React.ReactNode;
  /**
   * Give up on the remaining captures. The story stays published; readers just
   * fall back to building the document in their own browser.
   */
  skip: () => void;
}

/**
 * Render the story's PDF and per-scene map images once, at publish, in the
 * publishing author's browser.
 *
 * Why here and not on the backend: the capture pipeline is WebGL end to end —
 * it mounts a real mapbox-gl map per scene camera, waits for its `idle` event
 * and reads the canvas back. Nothing in a Django worker can do that. The
 * author's tab, on the other hand, already holds every map, tile and frame
 * array, and "Publishing…" is a place the wait is already expected.
 *
 * What it buys:
 *  - readers download a PDF that already exists instead of rebuilding it, so a
 *    single Download no longer costs ~20 anonymous Mapbox map loads;
 *  - embedded views render the captured stills, so an embed costs nothing on
 *    our access token at all.
 *
 * Best-effort by design: a story is published the moment the backend says so.
 * If this fails the story is still live — it just falls back to rendering the
 * document in the reader's browser, which is what it did before.
 */
export function usePublishAssets({
  storyMapId,
  storyTitle,
  content,
  scopeLabel,
  publishedAt,
  enabled,
  onFinished,
}: {
  storyMapId: string | undefined;
  storyTitle: string;
  /** The FROZEN content — assets must match what readers will see. */
  content: StoryContent | null | undefined;
  /** Byline fragment: "X project", "X folder" or "All projects". */
  scopeLabel: string | null | undefined;
  publishedAt: string | null | undefined;
  enabled: boolean;
  onFinished: (result: { ok: boolean }) => void;
}): PublishAssetsState {
  const [phase, setPhase] = useState<PublishAssetsPhase>("idle");
  const [detail, setDetail] = useState("");
  const startedRef = useRef(false);

  const uploadMedia = useUploadStoryMedia(storyMapId);
  const uploadPdf = useUploadStoryPdf();
  const attachImages = useAttachStorySceneImages();

  const scenes: StoryScene[] = useMemo(() => content?.scenes ?? [], [content]);

  // Published stories are read the same way readers read them: frozen sources,
  // no authed endpoints — so the captures match the published article.
  // Read the frozen content the way anonymous readers will: the published
  // bundle, no authed endpoints — so the captures match the published story.
  const sources = useStorySceneSources({ isPublic: true, content });

  const { captures, current, total, done, runner, secondsRemaining, cancel } =
    useSceneCaptures({
      scenes,
      sources,
      isPublic: true,
      enabled: enabled && !!content,
    });

  useEffect(() => {
    if (!enabled) {
      startedRef.current = false;
      setPhase("idle");
      return;
    }
    if (phase === "idle") {
      setPhase("capturing");
      setDetail("Rendering maps…");
    }
  }, [enabled, phase]);

  useEffect(() => {
    if (phase === "capturing" && !done) {
      const eta =
        secondsRemaining === null
          ? ""
          : secondsRemaining > 60
          ? ` · about ${Math.ceil(secondsRemaining / 60)} min left`
          : ` · about ${Math.max(secondsRemaining, 5)}s left`;
      setDetail(`Rendering map ${Math.min(current, total)} of ${total}…${eta}`);
    }
  }, [phase, current, total, done, secondsRemaining]);

  useEffect(() => {
    if (!enabled || !done || !content || !storyMapId || startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      try {
        setPhase("building_pdf");
        setDetail("Building the PDF…");

        // Dynamic import so the jsPDF stack stays out of the main bundle for
        // everyone who never publishes (see the lazy print route in App.tsx).
        const { buildStoryPdf } = await import("./buildStoryPdf");
        const doc = await buildStoryPdf({
          content,
          title: storyTitle,
          scopeLabel,
          projectNames: content.projects?.map((p) => p.name),
          author: [content.authorName, content.authorRole].filter(Boolean).join(", "),
          frozenDate: publishedAt ?? content.publishedAt,
          isDraft: false,
          captures,
          // Frozen animations carry their simulation name; readers have no
          // authed simulation list to look one up in.
          simulationName: () => null,
        });

        setPhase("uploading");
        setDetail("Saving the PDF…");
        await uploadPdf.mutateAsync({
          storyMapId,
          pdf: doc.output("blob") as Blob,
        });

        // Scene stills for embedded views. Uploaded one at a time through the
        // media endpoint (small bodies), then recorded in one call.
        const entries = Object.entries(captures).filter(
          (entry): entry is [string, string] => !!entry[1]
        );
        const images: Record<string, string> = {};
        for (let i = 0; i < entries.length; i += 1) {
          const [key, dataUrl] = entries[i];
          setDetail(`Saving map image ${i + 1} of ${entries.length}…`);
          const response = await uploadMedia.mutateAsync(dataUrl);
          const url = response.data?.url;
          if (url) images[key] = url;
        }
        if (Object.keys(images).length > 0) {
          await attachImages.mutateAsync({ storyMapId, images });
        }

        setPhase("done");
        setDetail("");
        onFinished({ ok: true });
      } catch {
        // The story is already published; these are an optimisation on top.
        setPhase("failed");
        setDetail("");
        onFinished({ ok: false });
      }
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, done, content, storyMapId]);

  return {
    phase,
    detail,
    runner: phase === "capturing" ? runner : null,
    skip: cancel,
  };
}
